import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";

import type { TextFieldProps } from "../../atoms/text-field";
import type { PhoneNumberFieldCountry } from "./types";

/**
 * Native: the country trigger's touch area, 44 tall (DS-028). The small field
 * is 40 and the trigger sits inside its 1pt border, so it's 38: it takes taps
 * 3pt past its top and bottom. The medium one's is 46 already.
 */
export const countryTriggerHitSlop = {
  sm: { top: 3, bottom: 3 },
  md: undefined,
} as const satisfies Record<NonNullable<TextFieldProps["size"]>, unknown>;

/**
 * The picked country's row fill in both lists: Select's selected option
 * (Figma's List Selector, Selected, 4759:19676), kept under the pointer, focus
 * and a press (PhoneNumberField #9)
 */
export const countryRowSelectedClassName = [
  "bg-luce-brand-primarySurface",
  "web:hover:bg-luce-brand-primarySurface web:focus:bg-luce-brand-primarySurface",
  "active:bg-luce-brand-primarySurface web:active:bg-luce-brand-primarySurface",
].join(" ");

export function ensurePhoneNumberWithPlus(value: string): string {
  if (!value) return "";
  const digitsOnly = value.replace(/[^\d]/g, "");
  if (!digitsOnly) return value.startsWith("+") ? "+" : "";
  return `+${digitsOnly}`;
}

export function getCountryByCode(
  countries: PhoneNumberFieldCountry[],
  code?: string | null,
): PhoneNumberFieldCountry | undefined {
  if (!code) return undefined;
  return countries.find((country) => country.code === code.toUpperCase());
}

/**
 * The country a phone number belongs to: the one libphonenumber-js names when
 * it can tell; otherwise `preferred` (the one the user picked) while the number
 * still starts with its calling code; otherwise the first country with it.
 * Countries share calling codes (+1, +44, +7), so without the preference
 * picking the United States showed American Samoa.
 */
export function inferCountryFromPhoneNumber(
  phoneNumber: string | undefined,
  countries: PhoneNumberFieldCountry[],
  preferred?: PhoneNumberFieldCountry,
): PhoneNumberFieldCountry | undefined {
  if (!phoneNumber) return undefined;
  const normalized = ensurePhoneNumberWithPlus(phoneNumber);
  if (!normalized) return undefined;

  const parsed = parsePhoneNumberFromString(normalized);
  if (parsed?.country) {
    return getCountryByCode(countries, parsed.country);
  }

  const digits = normalized.replace(/\D/g, "");
  const startsWithCode = (country: PhoneNumberFieldCountry) =>
    digits.startsWith(country.dialCode.replace("+", ""));
  if (preferred && startsWithCode(preferred)) return preferred;
  return countries.find(startsWithCode);
}

export function formatPhoneNumberForDisplay(phoneNumber: string | undefined): string {
  if (!phoneNumber) return "";

  try {
    return new AsYouType().input(phoneNumber);
  } catch {
    return phoneNumber;
  }
}

/**
 * Filter countries for the picker search box. Matches against country name
 * (case-insensitive substring) or dial code by digits — so "62", "+62", and
 * "indo" all surface Indonesia. An empty query returns the list unchanged.
 */
export function filterCountries(
  countries: PhoneNumberFieldCountry[],
  query: string,
): PhoneNumberFieldCountry[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return countries;

  const digits = trimmed.replace(/\D/g, "");

  return countries.filter((country) => {
    if (country.name.toLowerCase().includes(trimmed)) return true;
    if (digits && country.dialCode.replace(/\D/g, "").startsWith(digits)) return true;
    return false;
  });
}

export function buildPhoneNumberState(
  input: string,
  country: PhoneNumberFieldCountry,
): {
  phoneNumber: string;
} {
  const trimmedInput = input.trim();
  const isInternationalInput = trimmedInput.startsWith("+");

  if (isInternationalInput) {
    const normalizedInput = ensurePhoneNumberWithPlus(trimmedInput);

    if (!normalizedInput || normalizedInput === "+") {
      return {
        phoneNumber: normalizedInput,
      };
    }

    const formatter = new AsYouType();
    formatter.input(normalizedInput);
    const phoneNumber = formatter.getNumberValue() ?? normalizedInput;

    return {
      phoneNumber,
    };
  }

  const nationalNumber = trimmedInput.replace(/\D/g, "");
  if (!nationalNumber) {
    return {
      phoneNumber: "",
    };
  }

  const formatter = new AsYouType(country.code);
  formatter.input(nationalNumber);
  const phoneNumber = formatter.getNumberValue() ?? `${country.dialCode}${nationalNumber}`;

  return {
    phoneNumber,
  };
}
